import React, { useEffect, useState } from 'react';
import type { CatalogItem, PersonCreditsResponse } from '@lumos/contracts';
import { describeError } from '../api/client.js';
import { getPersonCredits } from '../api/endpoints.js';
import { Card } from '../components/Card.js';
import { GridSkeleton, RetryError } from '../components/Placeholders.js';
import { SectionWash, useWashItem } from '../components/SectionWash.js';

/** Пока фильмография не приехала: одна и та же ссылка, чтобы подложка не пересчитывалась. */
const NO_ITEMS: readonly CatalogItem[] = [];

/**
 * Фильмография человека — то, куда приводит нажатие на актёра в титрах.
 *
 * Порядок задаёт сервер: свежее первым, ещё не вышедшее в конце. Карьеру
 * читают с того, что человек делает сейчас, а витрина отдаёт роли по своей
 * популярности — это ответ на другой вопрос.
 */
export function PersonPage({ personId }: { personId: string }): React.JSX.Element {
  const [person, setPerson] = useState<PersonCreditsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Попытка загрузки: «Повторить» после ошибки заводит её заново. */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const abort = new AbortController();
    setPerson(null);
    setError(null);
    getPersonCredits(personId, abort.signal)
      .then(setPerson)
      .catch((e: unknown) => {
        if (abort.signal.aborted) return;
        setError(e instanceof Error ? describeError(e) : 'Не удалось открыть фильмографию.');
      });
    return () => abort.abort();
  }, [personId, attempt]);

  const { washItem, onFocusItem, onLeaveItem } = useWashItem(person?.items ?? NO_ITEMS);

  return (
    <main className="app">
      <SectionWash item={washItem} />
      <header className="person-head">
        {/* Фотография — не украшение: в титрах зритель нажимает на лицо, и
            страница обязана подтвердить, что открылся тот самый человек. */}
        <span className="person-photo">
          {person?.photo?.url != null ? (
            <img src={person.photo.url} alt="" loading="lazy" />
          ) : (
            <span className="person-initial" aria-hidden="true">
              {(person?.name ?? '?').slice(0, 1).toUpperCase()}
            </span>
          )}
        </span>
        <div>
          <h1 className="page-title">{person?.name ?? 'Фильмография'}</h1>
          {person?.knownFor != null && <p className="person-known">{person.knownFor}</p>}
        </div>
      </header>

      {person === null && error === null && <GridSkeleton />}
      {error !== null && <RetryError message={error} onRetry={() => setAttempt((value) => value + 1)} />}
      {person !== null && person.items.length === 0 && (
        <div className="empty-state" role="status">
          <h2>Фильмография пуста</h2>
          <p>{person.notice ?? 'Ролей не нашлось.'}</p>
        </div>
      )}

      {person !== null && person.items.length > 0 && (
        <div className="grid">
          {person.items.map((item) => (
            <Card key={item.id} item={item} onFocusItem={onFocusItem} onLeaveItem={onLeaveItem} />
          ))}
        </div>
      )}
    </main>
  );
}
